import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import api from "../../api";
import ChartInfoPopup from "./ChartInfoPopup";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const TotalActiveUsersChart = () => {
  const [data, setData] = useState({
    labels: ["1D", "1W", "1M", "3M", "6M", "1Y"],
    datasets: [],
  });

  const pathname = window.location.pathname;
  const isAdmin = pathname.startsWith("/admin");
  const isSuperadmin = pathname.startsWith("/superadmin");

  const [themeColors, setThemeColors] = useState({});

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const loadThemeColors = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      text: styles.getPropertyValue("--dark-color").trim(),
      grid: styles.getPropertyValue("--border-color").trim(),
      bg: styles.getPropertyValue("--background-color").trim(),
    };
  };

  useEffect(() => {
    setThemeColors(loadThemeColors());

    const observer = new MutationObserver(() => {
      setThemeColors(loadThemeColors());
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/auth/active-users-over-time`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const counts = res.data;

        // 👉 Build datasets dynamically
        let datasets = [];

        if (isSuperadmin) {
          datasets.push({
            label: "Super Admin",
            data: Object.values(counts.Superadmin),
            backgroundColor: "#D4D4D4",
            borderRadius: 4,
            barThickness: 20,
          });
        }

        datasets.push({
          label: "Admin",
          data: Object.values(counts.Admin),
          backgroundColor: "#6B7280",
          borderRadius: 4,
          barThickness: 20,
        });

        datasets.push({
          label: "Users",
          data: Object.values(counts.User),
          backgroundColor: "#000000",
          borderRadius: 4,
          barThickness: 20,
        });

        setData({
          labels: ["1D", "1W", "1M", "3M", "6M", "1Y"],
          datasets,
        });
      } catch (err) {
        console.error("Failed to fetch active users over time:", err);
      }
    };

    fetchData();
  }, [token]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: themeColors.text,
          font: {
            size: 13,
            weight: "500",
          },
        },
      },
      tooltip: {
        backgroundColor: themeColors.bg,
        titleColor: themeColors.text,
        bodyColor: themeColors.text,
        borderColor: themeColors.grid,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: { color: themeColors.text },
        grid: { color: themeColors.grid },

        // 👉 X-Axis Label
        title: {
          display: true,
          text: "Time Period",
          color: themeColors.text,
          font: {
            size: 13,
            weight: "bold",
          },
        },
      },
      y: {
        ticks: { color: themeColors.text },
        grid: { color: themeColors.grid },

        // 👉 Y-Axis Label
        title: {
          display: true,
          text: "Number of Users",
          color: themeColors.text,
          font: {
            size: 13,
            weight: "bold",
          },
        },
      },
    },
  };

  return (
    <div className="chart-card p-4 shadow-sm rounded profile mb-4">
      {/* <h4 className="fw-bold mb-4">
        <span className="border-start border-4 border-dark line refresh-prompts ps-2">
          Total Active Users
        </span>
      </h4> */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <h4 className="fw-bold mb-0">
          <span className="border-start border-4 border-dark ps-2 refresh-prompts">
            Total Active Users
          </span>
        </h4>

        <div className="chart-info-anchor">
          <ChartInfoPopup text="This clustered bar chart compares the number of active Admins and Users across different time ranges - Day, Week, Month, 3 Months, 6 Months, and 1 Year. The Y-axis showing activity counts and the X-axis showing the selected time. This chart makes it easy to understand overall engagement levels and clearly compare Admin vs. User activity across different durations." />
        </div>
      </div>
      <div style={{ height: "320px", width: "100%" }}>
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default TotalActiveUsersChart;
